const STORAGE_KEY = 'metis-map-cache-v2';
const LEGACY_GEOCODE_KEY = 'metis-geocode-v1';
const SNAPSHOT_PREFIX = 'metis-map-points:';

const ENTITY_TTL_MS = 30 * 86400000;
const GEOCODE_TTL_MS = 90 * 86400000;
const NEGATIVE_TTL_MS = 7 * 86400000;
const ROUTE_TTL_MS = 7 * 86400000;

const entityMem = new Map();
const geocodeMem = new Map();

let diskCache = { entities: {}, geocode: {} };

function now() {
  return Date.now();
}

function isFresh(entry, ttlMs) {
  return entry && Number.isFinite(entry.resolvedAt) && now() - entry.resolvedAt < ttlMs;
}

function loadDiskCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      diskCache = {
        entities: parsed.entities ?? {},
        geocode: parsed.geocode ?? {},
      };
    }
  } catch {
    diskCache = { entities: {}, geocode: {} };
  }

  try {
    const legacy = localStorage.getItem(LEGACY_GEOCODE_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      for (const [key, value] of Object.entries(parsed)) {
        if (value?.lat != null && value?.lng != null && !diskCache.geocode[key]) {
          diskCache.geocode[key] = { lat: value.lat, lng: value.lng, resolvedAt: now() };
        }
      }
    }
  } catch {
    /* ignore */
  }

  for (const [key, entry] of Object.entries(diskCache.entities)) {
    if (isFresh(entry, ENTITY_TTL_MS)) entityMem.set(key, entry);
  }
  for (const [key, entry] of Object.entries(diskCache.geocode)) {
    if (entry.failed ? isFresh(entry, NEGATIVE_TTL_MS) : isFresh(entry, GEOCODE_TTL_MS)) {
      geocodeMem.set(key, entry);
    }
  }
}

function persistDiskCache() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        v: 2,
        entities: diskCache.entities,
        geocode: diskCache.geocode,
      }),
    );
  } catch {
    /* quota */
  }
}

loadDiskCache();

export function entityCacheKey(instanceId, entityType, entityId) {
  return `${instanceId}:${entityType}:${entityId}`;
}

export function normalizeGeocodeQuery(query) {
  return String(query ?? '').trim().toLowerCase();
}

export function getEntityCoords(instanceId, entityType, entityId, entityUpdatedAt = null) {
  const key = entityCacheKey(instanceId, entityType, entityId);
  const entry = entityMem.get(key) ?? diskCache.entities[key];
  if (!entry || !isFresh(entry, ENTITY_TTL_MS)) return null;
  if (entityUpdatedAt && entry.entityUpdatedAt && entry.entityUpdatedAt !== entityUpdatedAt) {
    return null;
  }
  if (entry.failed) return null;
  if (Number.isFinite(entry.lat) && Number.isFinite(entry.lng)) {
    return { lat: entry.lat, lng: entry.lng };
  }
  return null;
}

export function setEntityCoords(instanceId, entityType, entityId, coords, { source = 'geocode', entityUpdatedAt = null, failed = false } = {}) {
  const key = entityCacheKey(instanceId, entityType, entityId);
  const entry = failed
    ? { failed: true, resolvedAt: now(), entityUpdatedAt }
    : { lat: coords.lat, lng: coords.lng, source, resolvedAt: now(), entityUpdatedAt };
  entityMem.set(key, entry);
  diskCache.entities[key] = entry;
  persistDiskCache();
}

export function invalidateEntityCoords(instanceId, entityType, entityId) {
  const key = entityCacheKey(instanceId, entityType, entityId);
  entityMem.delete(key);
  delete diskCache.entities[key];
  persistDiskCache();
}

export function invalidateInstanceMapCache(instanceId, dayKey = null) {
  for (const key of Object.keys(diskCache.entities)) {
    if (key.startsWith(`${instanceId}:`)) {
      entityMem.delete(key);
      delete diskCache.entities[key];
    }
  }
  if (dayKey) {
    clearRouteCache(instanceId, dayKey).catch(() => {});
  } else {
    clearAllRouteCache(instanceId).catch(() => {});
  }
  persistDiskCache();
  clearPointsSnapshot(instanceId);
}

export function getGeocodeCache(query) {
  const key = normalizeGeocodeQuery(query);
  if (!key) return { hit: false, coords: null, failed: false };

  const entry = geocodeMem.get(key) ?? diskCache.geocode[key];
  if (!entry) return { hit: false, coords: null, failed: false };

  if (entry.failed) {
    if (isFresh(entry, NEGATIVE_TTL_MS)) return { hit: true, coords: null, failed: true };
    geocodeMem.delete(key);
    delete diskCache.geocode[key];
    persistDiskCache();
    return { hit: false, coords: null, failed: false };
  }

  if (isFresh(entry, GEOCODE_TTL_MS) && Number.isFinite(entry.lat) && Number.isFinite(entry.lng)) {
    return { hit: true, coords: { lat: entry.lat, lng: entry.lng }, failed: false };
  }

  return { hit: false, coords: null, failed: false };
}

export function setGeocodeCache(query, coords, { failed = false } = {}) {
  const key = normalizeGeocodeQuery(query);
  if (!key) return;

  const entry = failed
    ? { failed: true, resolvedAt: now() }
    : { lat: coords.lat, lng: coords.lng, resolvedAt: now() };

  geocodeMem.set(key, entry);
  diskCache.geocode[key] = entry;
  persistDiskCache();

  if (!failed && coords) {
    try {
      const legacy = JSON.parse(localStorage.getItem(LEGACY_GEOCODE_KEY) || '{}');
      legacy[key] = coords;
      localStorage.setItem(LEGACY_GEOCODE_KEY, JSON.stringify(legacy));
    } catch {
      /* ignore */
    }
  }
}

// ---------------------------------------------------------------------------
// IndexedDB — OSRM route cache
// ---------------------------------------------------------------------------

const DB_NAME = 'metis-map-cache';
const DB_VERSION = 1;
const ROUTE_STORE = 'routes';

function openRouteDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ROUTE_STORE)) {
        db.createObjectStore(ROUTE_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function routeCacheKey(instanceId, dayKey, coordHash) {
  return `${instanceId}:route:${dayKey ?? 'all'}:${coordHash}`;
}

export function hashCoords(coordList) {
  return coordList
    .map((c) => `${c.lat.toFixed(5)},${c.lng.toFixed(5)}`)
    .join('|');
}

export async function getRouteCache(instanceId, dayKey, coordHash) {
  try {
    const db = await openRouteDb();
    const key = routeCacheKey(instanceId, dayKey, coordHash);
    return await new Promise((resolve) => {
      const tx = db.transaction(ROUTE_STORE, 'readonly');
      const req = tx.objectStore(ROUTE_STORE).get(key);
      req.onsuccess = () => {
        const entry = req.result;
        if (entry && isFresh(entry, ROUTE_TTL_MS)) resolve(entry.data);
        else resolve(null);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function setRouteCache(instanceId, dayKey, coordHash, data) {
  try {
    const db = await openRouteDb();
    const key = routeCacheKey(instanceId, dayKey, coordHash);
    await new Promise((resolve, reject) => {
      const tx = db.transaction(ROUTE_STORE, 'readwrite');
      tx.objectStore(ROUTE_STORE).put({ data, resolvedAt: now() }, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* ignore */
  }
}

export async function clearRouteCache(instanceId, dayKey) {
  try {
    const db = await openRouteDb();
    const prefix = `${instanceId}:route:${dayKey ?? 'all'}:`;
    await new Promise((resolve) => {
      const tx = db.transaction(ROUTE_STORE, 'readwrite');
      const store = tx.objectStore(ROUTE_STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          if (String(cursor.key).startsWith(prefix)) cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* ignore */
  }
}

async function clearAllRouteCache(instanceId) {
  try {
    const db = await openRouteDb();
    const prefix = `${instanceId}:route:`;
    await new Promise((resolve) => {
      const tx = db.transaction(ROUTE_STORE, 'readwrite');
      const store = tx.objectStore(ROUTE_STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          if (String(cursor.key).startsWith(prefix)) cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Session snapshot — instant paint when switching tabs
// ---------------------------------------------------------------------------

function snapshotKey(instanceId, dayKey) {
  return `${SNAPSHOT_PREFIX}${instanceId}:${dayKey ?? 'all'}`;
}

export function loadPointsSnapshot(instanceId, dayKey) {
  try {
    const raw = sessionStorage.getItem(snapshotKey(instanceId, dayKey));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function savePointsSnapshot(instanceId, dayKey, points) {
  try {
    sessionStorage.setItem(snapshotKey(instanceId, dayKey), JSON.stringify(points));
  } catch {
    /* ignore */
  }
}

export function clearPointsSnapshot(instanceId) {
  try {
    const prefix = `${SNAPSHOT_PREFIX}${instanceId}:`;
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore */
  }
}
